import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {otherSystemCategoryLabel} from "../../utils/otherSystemCategoryPresentation.js";
import {sourceMarketingFlagReportValue} from "../../utils/otherSystemMarketingFlag.js";
import {formatReportingDateRange} from "../../utils/reportingDateFormat.js";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatCurrencyItemTooltip,
    filterRowsBySearch,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    TablePagination,
    TableSearch,
} from "./ReportingLab.jsx";

function typeTranslationKey(type) {
    return `other-sources.types.${type}`;
}

function typeLabel(t, type) {
    if (type === "Other") {
        return t("reporting_lab.other_costs.type_other", {defaultValue: "Ostatní"});
    }

    return t(typeTranslationKey(type), {defaultValue: type});
}

function groupSpend(rows, keyGetter, fallbackLabel) {
    const map = new Map();

    rows.forEach((row) => {
        const key = keyGetter(row) || fallbackLabel;
        map.set(key, (map.get(key) ?? 0) + moneyAmount(row.allocatedSpend));
    });

    return [...map.entries()]
        .map(([name, amount]) => ({name, amount}))
        .sort((a, b) => b.amount - a.amount);
}

export default function ReportingOtherCostsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const [rows, setRows] = useState([]);
    const [sourceCount, setSourceCount] = useState(0);
    const [allocatedSpend, setAllocatedSpend] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setRows([]);
        setSourceCount(0);
        setAllocatedSpend(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());
        api.otherCostsPerformance({
            projectId: project.id,
            erpFilter: {
                since: sinceString,
                until: untilString,
                selectedCentreIds: centresAllowListed ?? [],
                selectedDomainIds: domainsAllowListed ?? [],
            },
        }).then((data) => {
                if (!active) {
                    return;
                }
                setRows(data?.rows ?? []);
                setSourceCount(data?.sourceCount ?? 0);
                setAllocatedSpend(data?.allocatedSpend ?? null);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, sinceString, untilString, centreFilterKey, domainFilterKey]);

    const currency = allocatedSpend?.currency ?? projectCurrency(project);
    const totalSpend = moneyAmount(allocatedSpend);
    const unassignedRows = rows.filter((row) => !row.domainId || !row.centreId);
    const spendByType = groupSpend(rows, (row) => t(typeTranslationKey(row.type), {defaultValue: row.type}), t("reporting_lab.other_costs.unassigned", {defaultValue: "Nezařazeno"}));
    const spendBySource = groupSpend(rows, (row) => row.sourceName, t("reporting_lab.other_costs.unnamed_source", {defaultValue: "Nepojmenovaný zdroj"})).slice(0, 10);
    const spendByDomain = groupSpend(rows, (row) => row.domainName, t("reporting_lab.other_costs.missing_domain", {defaultValue: "Bez domény"})).slice(0, 10);
    const filteredRows = filterRowsBySearch(rows, tableSearch, (row) => [row.sourceName, row.type, row.domainName, row.centreName, row.note]);
    const sortedRows = [...filteredRows].sort((a, b) => moneyAmount(b.allocatedSpend) - moneyAmount(a.allocatedSpend));
    const tablePages = Math.max(1, Math.ceil(sortedRows.length / tablePageSize));
    const normalizedTablePage = Math.min(tablePage, tablePages);
    const visibleRows = sortedRows.slice((normalizedTablePage - 1) * tablePageSize, normalizedTablePage * tablePageSize);

    useEffect(() => {
        setTablePage(1);
    }, [sinceString, untilString, centreFilterKey, domainFilterKey]);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const typeOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${params.percent?.toFixed(1).replace(".", ",")} %`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: spendByType.map((row) => ({
                    name: row.name,
                    value: row.amount / 100,
                })),
            },
        ],
    };

    const sourceOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 28, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...spendBySource].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.other_costs.spend", {defaultValue: "Náklad"}),
                type: "bar",
                data: [...spendBySource].reverse().map((row, index) => ({
                    value: row.amount / 100,
                    itemStyle: {color: ChannelMixColors[(spendBySource.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 24,
            },
        ],
    };

    const domainOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 58, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: spendByDomain.map((row) => row.name),
            axisLabel: {fontSize: 12, rotate: spendByDomain.length > 5 ? 18 : 0},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: [
            {
                name: t("reporting_lab.other_costs.spend", {defaultValue: "Náklad"}),
                type: "bar",
                data: spendByDomain.map((row, index) => ({
                    value: row.amount / 100,
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 34,
            },
        ],
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.other_costs", {defaultValue: "Ostatní náklady"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.other_costs.title", {defaultValue: "K čemu slouží ostatní náklady"})}
                >
                    {t("reporting_lab.descriptions.other_costs.text", {defaultValue: "Kontroluje ručně evidované nebo externí marketingové náklady jako mailing, affiliate a další zdroje, včetně vazby na domény a střediska."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.other_costs.total_spend", {defaultValue: "Ostatní náklady"})} value={renderCurrencyValue({amount: totalSpend, currency}, true)} meta={t("reporting_lab.other_costs.prorated_period", {defaultValue: "Poměrně za vybrané období"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.other_costs.sources", {defaultValue: "Zdroje"})} value={renderNumber(sourceCount)} meta={t("reporting_lab.other_costs.configured_sources", {defaultValue: "Nastavené zdroje"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.other_costs.records", {defaultValue: "Záznamy"})} value={renderNumber(rows.length)} meta={t("reporting_lab.other_costs.overlap_records", {defaultValue: "S průnikem období"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.other_costs.mapping_issues", {defaultValue: "Problémy vazeb"})} value={renderNumber(unassignedRows.length)} meta={t("reporting_lab.other_costs.missing_domain_or_centre", {defaultValue: "Bez domény nebo střediska"})} tone={unassignedRows.length > 0 ? "warn" : "good"}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.other_costs.spend_by_source", {defaultValue: "Náklady podle zdroje"})} className="col-span-12 xl:col-span-7">
                    {rows.length > 0
                        ? <EChart option={sourceOption} className="h-96"/>
                        : <EmptyChartState>{t("reporting_lab.other_costs.empty_chart", {defaultValue: "Pro vybrané období nejsou dostupné žádné ostatní náklady."})}</EmptyChartState>}
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.other_costs.spend_by_type", {defaultValue: "Podíl typů nákladů"})} className="col-span-12 xl:col-span-5">
                    {rows.length > 0
                        ? <EChart option={typeOption} className="h-96"/>
                        : <EmptyChartState>{t("reporting_lab.other_costs.empty_chart", {defaultValue: "Pro vybrané období nejsou dostupné žádné ostatní náklady."})}</EmptyChartState>}
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.other_costs.spend_by_domain", {defaultValue: "Náklady podle domény"})} className="col-span-12 xl:col-span-7">
                    {rows.length > 0
                        ? <EChart option={domainOption} className="h-80"/>
                        : <EmptyChartState compact>{t("reporting_lab.other_costs.empty_chart", {defaultValue: "Pro vybrané období nejsou dostupné žádné ostatní náklady."})}</EmptyChartState>}
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.other_costs.mapping_review", {defaultValue: "Kontrola vazeb"})} className="col-span-12 xl:col-span-5">
                    <div className="space-y-3">
                        {unassignedRows.slice(0, 8).map((row) => <div key={row.id} className="app-surface rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30">
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-semibold text-slate-900 dark:text-slate-100">{row.sourceName ?? "-"}</div>
                                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{renderCurrencyValue(row.allocatedSpend, true)}</div>
                            </div>
                            <div className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                                {!row.domainId && t("reporting_lab.other_costs.missing_domain", {defaultValue: "Bez domény"})}
                                {!row.domainId && !row.centreId ? " · " : ""}
                                {!row.centreId && t("reporting_lab.other_costs.missing_centre", {defaultValue: "Bez střediska"})}
                            </div>
                        </div>)}
                        {rows.length === 0 && <div className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.other_costs.no_mapping_records", {defaultValue: "Ve vybraném období nejsou žádné záznamy ke kontrole vazeb."})}</div>}
                        {rows.length > 0 && unassignedRows.length === 0 && <div className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.other_costs.no_mapping_issues", {defaultValue: "Všechny nákladové zdroje v období mají vazbu na doménu i středisko."})}</div>}
                    </div>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.other_costs.table", {defaultValue: "Přehled nákladů"})} className="col-span-12">
                    <TableSearch value={tableSearch} onChange={(value) => { setTableSearch(value); setTablePage(1); }} visible={rows.length > 15} t={t}/>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.source", {defaultValue: "Zdroj"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.type", {defaultValue: "Typ"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.category", {defaultValue: "Kategorie"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.in_marketing_costs", {defaultValue: "V marketingových nákladech"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.domain", {defaultValue: "Doména"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.centre", {defaultValue: "Středisko"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.other_costs.period", {defaultValue: "Období"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.other_costs.spend", {defaultValue: "Náklad"})}</th>
                                <th className="py-2 text-left font-semibold">{t("reporting_lab.other_costs.note", {defaultValue: "Poznámka"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {visibleRows.map((row) => <tr key={row.id}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.sourceName ?? "-"}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{typeLabel(t, row.type)}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{otherSystemCategoryLabel(t, row.category)}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{
                                    sourceMarketingFlagReportValue(row.type, row.includeInMarketingCosts) === null
                                        ? "-"
                                        : sourceMarketingFlagReportValue(row.type, row.includeInMarketingCosts)
                                            ? t("reporting_lab.other_costs.in_marketing_costs_yes")
                                            : t("reporting_lab.other_costs.in_marketing_costs_no")
                                }</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{row.domainName ?? "-"}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{row.centreName ?? "-"}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300"><strong>{formatReportingDateRange(row.overlapSince, row.overlapUntil)}</strong> ({row.allocatedDays}/{row.sourceDays})</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.allocatedSpend, true)}</td>
                                <td className="py-3 text-slate-600 dark:text-slate-300">{row.note ?? "-"}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination page={normalizedTablePage} pageSize={tablePageSize} totalItems={sortedRows.length} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>
                    {sortedRows.length === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.other_costs.empty", {defaultValue: "Pro vybrané období nejsou dostupné žádné ostatní náklady."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}

function EmptyChartState({children, compact = false}) {
    return (
        <div className={`flex items-center justify-center px-6 text-center text-sm text-slate-500 dark:text-slate-400 ${compact ? "h-80" : "h-96"}`}>
            {children}
        </div>
    );
}
